import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/browser";
import Icon from "../Icon";
import { fmtCcy, fmtNum, fmtPrice, fmtUnits, formatFriendly } from "../helpers";
import { ApiEntryDrift, ReconRow } from "../types";
import { request } from "../../../helpers/utilities";
import { GET_RECONCILIATION_ENTRY_DRIFT } from "../../../gql/productStatements";
import MatchPill from "./MatchPill";
import NarrationSection from "./NarrationSection";
import SupersededTab from "./SupersededTab";
import { MATCH_LABELS } from "./FilterPopover";

const driftCache: Record<string, ApiEntryDrift> = {};

const SHOW_COMPANIES = false;

interface Props {
  row: ReconRow;
  onClose: () => void;
}

const RowDetailDrawer: React.FC<Props> = ({ row, onClose }) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "summary" | "companies" | "transactions" | "superseded"
  >("summary");
  const [drift, setDrift] = useState<ApiEntryDrift | null>(null);
  const [driftError, setDriftError] = useState<string | null>(null);

  // Cached by entry id, which is safe because everything fetched here is
  // immutable for that id: snapshots are written once when the run is created,
  // and a later rerun mints a new entry rather than altering this one.
  useEffect(() => {
    let current = true;

    setDrift(null);
    setDriftError(null);

    if (!row.id) return undefined;

    if (driftCache[row.id]) {
      setDrift(driftCache[row.id]);
      return undefined;
    }

    request({
      query: GET_RECONCILIATION_ENTRY_DRIFT,
      variables: { id: row.id },
    })
      .then(
        (response: {
          data: { data: { reconciliationEntry: ApiEntryDrift } };
        }) => {
          const data = response?.data?.data?.reconciliationEntry;
          if (!data) return;
          driftCache[data.id] = data;
          // Guards against a slower response for a previously opened row
          // landing after this one and overwriting it.
          if (current) setDrift(data);
        },
      )
      .catch((error: unknown) => {
        Sentry.captureException(error);
        if (current) setDriftError("We couldn’t load what this run replaced.");
      });

    return () => {
      current = false;
    };
  }, [row.id]);

  const supersededCount = drift?.supersededEntries.length ?? 0;
  const diffAmount = row.diff ?? 0;
  const diffUnits = row.diffUnits ?? 0;
  const productColorVal = `hsl(${
    (row.product.length * 73 + 200) % 360
  } 65% 55%)`;
  return (
    <>
      <div className="drawer-backdrop open" onClick={onClose} />
      <aside
        className="drawer drawer-wide open"
        style={
          isMaximized
            ? {
                width: "100vw",
                maxWidth: "100vw",
                height: "100vh",
                maxHeight: "100vh",
                top: 0,
                bottom: 0,
                right: 0,
                left: 0,
                transform: "translateX(0)",
              }
            : undefined
        }
      >
        <div className="drawer-head">
          <div>
            <div
              className="drawer-sub"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <span
                className="product-dot"
                style={{ background: productColorVal }}
              />
              <span>{row.product}</span>
              {row.fundCode && (
                <span
                  className="mono"
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: "var(--surface-2)",
                    color: "var(--ink-2)",
                  }}
                >
                  {row.fundCode}
                </span>
              )}
            </div>
            <div className="drawer-title">
              Price entry · {formatFriendly(row.priceDate)}
              <span
                style={{
                  fontSize: 13,
                  color: "var(--muted)",
                  fontWeight: "normal",
                  marginLeft: 8,
                }}
              >
                ({row.companiesCount ?? 0}{" "}
                {row.companiesCount === 1 ? "company" : "companies"})
              </span>
            </div>
            <div
              style={{
                marginTop: 10,
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <MatchPill value={row.match} />
              <span style={{ color: "var(--muted)", fontSize: 12.5 }}>
                Received {formatFriendly(row.created, true)} ·{" "}
                {MATCH_LABELS[row.match]}
              </span>
            </div>
            {row.reversedAt && (
              <div
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  background: "var(--surface-2)",
                  borderRadius: "var(--radius)",
                  fontSize: 12.5,
                  color: "var(--muted)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Icon name="refresh" size={13} strokeWidth={2.2} />
                <span>
                  <strong style={{ color: "var(--ink-2)" }}>Reversed</strong> on{" "}
                  {formatFriendly(row.reversedAt, true)}
                  {row.reversedFromUploadId && (
                    <>
                      {" "}
                      via{" "}
                      <strong style={{ color: "var(--ink-2)" }}>
                        {row.reversedFromUploadId}
                      </strong>
                    </>
                  )}
                  . This entry is no longer Phoenix&rsquo;s source of truth for
                  this product and date.
                </span>
              </div>
            )}
          </div>
          <div
            style={{
              position: "absolute",
              right: 24,
              top: 24,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button
              onClick={() => setIsMaximized(!isMaximized)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 8,
                borderRadius: 4,
                border: "none",
                background: "none",
                color: "var(--muted)",
                cursor: "pointer",
              }}
              title={
                isMaximized ? "Restore window size" : "Maximize window size"
              }
            >
              <Icon
                name={isMaximized ? "minimize" : "maximize"}
                size={16}
                strokeWidth={2}
              />
            </button>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 8,
                borderRadius: 4,
                border: "none",
                background: "none",
                color: "var(--muted)",
                cursor: "pointer",
              }}
            >
              <Icon name="x" size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="drawer-body">
          {/* Tab navigation */}
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid var(--border)",
              marginBottom: 16,
              gap: 20,
              position: "sticky",
              top: -20,
              background: "var(--surface)",
              zIndex: 10,
              paddingTop: 12,
              paddingBottom: 2,
            }}
          >
            {(["summary", "companies", "transactions", "superseded"] as const)
              .filter((tab) => tab !== "companies" || SHOW_COMPANIES)
              .filter((tab) => tab !== "superseded" || supersededCount > 0)
              .map((tab) => {
                const label =
                  tab === "summary"
                    ? "📋 Summary"
                    : tab === "companies"
                    ? `🏢 Companies Breakdown (${row.companiesCount ?? 0})`
                    : tab === "superseded"
                    ? `🕓 Superseded (${supersededCount})`
                    : `🔍 All Transactions (${row.txCount ?? 0})`;
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: "8px 12px 10px",
                      background: "none",
                      border: "none",
                      borderRadius: 0,
                      borderBottom: isActive
                        ? "2.5px solid var(--enterprise-navy)"
                        : "2.5px solid transparent",
                      color: isActive ? "var(--ink)" : "var(--muted)",
                      fontWeight: isActive ? 600 : "normal",
                      cursor: "pointer",
                      outline: "none",
                      fontSize: 13.5,
                      transition: "var(--transition-all-200)",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
          </div>

          {activeTab === "summary" && (
            <>
              <div className="section-label">Price details</div>
              <div className="detail-grid">
                <div className="detail-cell">
                  <div className="detail-key">Unit price</div>
                  <div className="detail-val mono">{fmtPrice(row.price)}</div>
                </div>
                <div className="detail-cell">
                  <div className="detail-key">
                    Reconciled TX (TA & Phoenix) / Phoenix Upload
                  </div>
                  <div className="detail-val mono">
                    {fmtNum(row.expectedPaymentCount)} / {fmtNum(row.txCount)}
                  </div>
                </div>
                <div className="detail-cell">
                  <div className="detail-key">Upload date</div>
                  <div className="detail-val">
                    {formatFriendly(row.priceDate)}
                  </div>
                </div>
                <div className="detail-cell">
                  <div className="detail-key">Payments date</div>
                  <div
                    className="detail-val"
                    style={{
                      color:
                        row.match === "pending" ? "var(--muted)" : "inherit",
                    }}
                  >
                    {row.match === "pending"
                      ? "—"
                      : formatFriendly(row.paymentsPriceDate || row.priceDate)}
                  </div>
                </div>
              </div>

              <div className="section-label" style={{ marginTop: 22 }}>
                Upload vs payments
              </div>
              <div className="compare-tbl">
                <div className="compare-row compare-head">
                  <div></div>
                  <div>Upload</div>
                  <div>Payments</div>
                  <div>Difference</div>
                </div>
                <div className="compare-row">
                  <div>Total amount (GHS)</div>
                  <div className="mono">{fmtCcy(row.amount)}</div>
                  <div className="mono">{fmtCcy(row.amountTA)}</div>
                  <div
                    className="mono"
                    style={{
                      color:
                        Math.abs(diffAmount) > 0.01
                          ? "var(--danger)"
                          : "var(--muted)",
                      fontWeight: 600,
                    }}
                  >
                    {Math.abs(diffAmount) < 0.005
                      ? "0.00"
                      : (diffAmount > 0 ? "+" : "−") +
                        fmtCcy(Math.abs(diffAmount))}
                  </div>
                </div>
                <div className="compare-row">
                  <div>Units</div>
                  <div className="mono">{fmtUnits(row.unitsPhx)}</div>
                  <div className="mono">{fmtUnits(row.unitsTA)}</div>
                  <div
                    className="mono"
                    style={{
                      color:
                        Math.abs(diffUnits) > 0.01
                          ? "var(--danger)"
                          : "var(--muted)",
                      fontWeight: 600,
                    }}
                  >
                    {Math.abs(diffUnits) < 0.005
                      ? "0.00"
                      : (diffUnits > 0 ? "+" : "−") +
                        fmtUnits(Math.abs(diffUnits))}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "superseded" ? (
            <SupersededTab row={row} drift={drift} error={driftError} />
          ) : (
            <NarrationSection row={row} activeTab={activeTab} />
          )}
        </div>
        <div className="drawer-foot">
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </aside>
    </>
  );
};

export default RowDetailDrawer;
